/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-15 09:07:55
 *
 *******************************************************/
// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { downloadFile } from 'src/helpers';

import apiService from 'src/services/api-service';
import {
  GET_STOP_WORDS_REQUEST,
  GET_STOP_WORDS_SUCCESS,
  GET_STOP_WORDS_FAILURE,
  UPDATE_STOP_WORDS_REQUEST,
  UPDATE_STOP_WORDS_SUCCESS,
  UPDATE_STOP_WORDS_FAILURE,
  VERIFY_CONTENT_BEFORE_IMPORT_STOP_WORDS_REQUEST,
  VERIFY_CONTENT_BEFORE_IMPORT_STOP_WORDS_SUCCESS,
  VERIFY_CONTENT_BEFORE_IMPORT_STOP_WORDS_FAILURE,
  IMPORT_FILE_STOP_WORDS_REQUEST,
  IMPORT_FILE_STOP_WORDS_SUCCESS,
  IMPORT_FILE_STOP_WORDS_FAILURE,
  EXPORT_FILE_STOP_WORDS_REQUEST,
  EXPORT_FILE_STOP_WORDS_SUCCESS,
  EXPORT_FILE_STOP_WORDS_FAILURE
} from 'stores/flow-type/stop-words.type';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get stop words api
 * @return {object} The RSAA object for API middleware
 */
export const getStopWords = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.stop-words.get-stop-words', {}, [
    GET_STOP_WORDS_REQUEST,
    {
      type: GET_STOP_WORDS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_STOP_WORDS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.StopWords.Actions.getStopWords'),
          i18n
        )
    }
  ]);
};

/**
 * call to update stop words api
 * @return {object} The RSAA object for API middleware
 */
export const updateStopWords = (data: Array<string>, i18n: I18n) => {
  // Prepare Body
  const dataBody = { data };

  return apiService.sendRequest('admin-v3.stop-words.update-stop-words', { body: dataBody }, [
    UPDATE_STOP_WORDS_REQUEST,
    {
      type: UPDATE_STOP_WORDS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.StopWords.Actions.updateStopWords_Success'),
          i18n
        );
        mixpanelV3Helper.trackEventData('Edit Stop Words', {
          feature: 'Stop Words',
          noStopWords: data.total
        });

        // Track Full Story event
        trackFSEvent('Edit Stop Words', {
          feature: 'Stop Words',
          noStopWords: data.total
        });

        return { data };
      }
    },
    {
      type: UPDATE_STOP_WORDS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.StopWords.Actions.updateStopWords_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to verify file content before import
 * @return {object} The RSAA object for API middleware
 */
export const verifyFileContent = files => {
  return apiService.sendRequest(
    'admin-v3.stop-words.verify-import-stop-words',
    { attachements: files },
    [
      VERIFY_CONTENT_BEFORE_IMPORT_STOP_WORDS_REQUEST,
      {
        type: VERIFY_CONTENT_BEFORE_IMPORT_STOP_WORDS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: VERIFY_CONTENT_BEFORE_IMPORT_STOP_WORDS_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { ...data };
        }
      }
    ]
  );
};

/**
 * call to import file content
 * @return {object} The RSAA object for API middleware
 */
export const importFile = (files, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.stop-words.import-stop-words', { attachements: files }, [
    IMPORT_FILE_STOP_WORDS_REQUEST,
    {
      type: IMPORT_FILE_STOP_WORDS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        mixpanelV3Helper.trackEventData('Import Stop Words', {
          feature: 'Stop Words'
        });

        // Track Full Story event
        trackFSEvent('Import Stop Words', {
          feature: 'Stop Words'
        });

        return { data };
      }
    },
    {
      type: IMPORT_FILE_STOP_WORDS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.StopWords.Actions.importFile'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to download file content
 * @return {object} The RSAA object for API middleware
 */
export const exportFile = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.stop-words.export-stop-words', {}, [
    EXPORT_FILE_STOP_WORDS_REQUEST,
    {
      type: EXPORT_FILE_STOP_WORDS_SUCCESS,
      payload: async (action, state, res) => {
        // Convert response data into blob and download
        const blob = await res.blob();
        downloadFile(blob, 'All stop words data.csv');

        const data = await getJSON(res);

        mixpanelV3Helper.trackEventData('Export Stop Words', {
          feature: 'Stop Words'
        });

        // Track Full Story event
        trackFSEvent('Export Stop Words', {
          feature: 'Stop Words'
        });

        return { data };
      }
    },
    {
      type: EXPORT_FILE_STOP_WORDS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.StopWords.Actions.exportFile'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};
